export type VariantMixEvidence = {
  title_variant_tokens?: Record<string, string[]>;
  variant_options?: Record<string, string[]>;
};

export type VariantMixAnnotationInput = {
  identity_mixed?: boolean | null;
  mixed_axes?: string[] | null;
  variant_mixed_evidence?: VariantMixEvidence | null;
};

const MODEL_DEFINING_AXES = new Set(['generation', 'cpu', 'chip']);

function axisLabel(axis: string): string {
  if (axis === 'ram' || axis === 'cpu') return axis.toUpperCase();
  return axis
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatAxisList(axes: string[]): string {
  return axes.map(axisLabel).join('/');
}

function valueLabel(value: string): string {
  return value
    .replace(/(\d+)\s*gb\b/gi, '$1GB')
    .replace(/(\d+)\s*tb\b/gi, '$1TB')
    .replace(/\bm([1-4])\b/gi, 'M$1')
    .replace(/\bi([3579])\b/gi, 'i$1');
}

function observedValuesByAxis(data: VariantMixAnnotationInput): Record<string, string[]> {
  const evidence = data.variant_mixed_evidence || {};
  const titleTokens = evidence.title_variant_tokens || {};
  const variantOptions = evidence.variant_options || {};
  const result: Record<string, string[]> = {};

  for (const axis of data.mixed_axes || []) {
    const values = titleTokens[axis] || variantOptions[axis] || [];
    const unique = Array.from(new Set(values.map(valueLabel))).filter(Boolean);
    if (unique.length > 0) {
      result[axis] = unique;
    }
  }

  return result;
}

export function buildVariantMixAnnotation(data: VariantMixAnnotationInput): string | null {
  if (!data.identity_mixed) return null;

  const axes = data.mixed_axes || [];
  if (axes.length === 0) return null;

  const observed = observedValuesByAxis(data);
  const isModelDefining = axes.some((axis) => MODEL_DEFINING_AXES.has(axis));

  if (axes.length >= 3 || isModelDefining) {
    const observedValues = Object.values(observed).flat();
    const hasIntelFamily = observedValues.some(
      (value) => /^i[3579]$/i.test(value) || /intel/i.test(value),
    );
    const hasMSeries = observedValues.some((value) => /^m[1-4]/i.test(value));
    const coverage =
      hasIntelFamily && hasMSeries ? 'Intel & M-series configurations' : 'configurations';
    return coverage
      ? `Across related models - covers a range of ${coverage}.`
      : 'Across related models.';
  }

  const observedText = axes
    .map((axis) => {
      const values = observed[axis];
      if (!values || values.length === 0) return null;
      return axes.length === 1
        ? values.join(', ')
        : `${axisLabel(axis)}: ${values.join(', ')}`;
    })
    .filter((value): value is string => value != null)
    .join('; ');

  return observedText
    ? `Market Price - reflects ${formatAxisList(axes)} options (${observedText}).`
    : `Market Price - reflects ${formatAxisList(axes)} options.`;
}
